import * as React from "react";
import { useCallback, memo, useEffect, useState, useRef } from "react";
import { getVipList } from "../../api/vip";
import { useGlobalValue } from "../../stores/GlobalState";
import { setGlobalValue } from "../../stores/GlobalState";
import { useT } from "../../i18n/hooks";
import type {
  NavigationItem,
  BottomNavigationProps,
} from "./BottomNavigation.types";
import "./BottomNavigation.scss";
import inviteImg from "@/assets/homePage/reskin/invite.webp";
import inviteActiveImg from "@/assets/homePage/reskin/invite-active.webp";
import eventsImg from "@/assets/homePage/reskin/events.png";
import eventsActiveImg from "@/assets/homePage/reskin/events-active.webp";
import homeImg from "@/assets/homePage/reskin/home.webp";
import homeActiveImg from "@/assets/homePage/reskin/home-active.webp";
import rewardsImg from "@/assets/homePage/reskin/rewards.webp";
import rewardsActiveImg from "@/assets/homePage/reskin/rewards-active.webp";
import accountImg from "@/assets/homePage/reskin/account.webp";
import accountActiveImg from "@/assets/homePage/reskin/account-active.webp";

// Invite · Events · Home · Rewards · Account（仅 reskin 图标与从左到右资源顺序对应）
const defaultItems: NavigationItem[] = [
  {
    id: "agent-center",
    name: "Invite",
    icon: "invite",
    iconDefault: inviteImg,
    iconActive: inviteActiveImg,
    badge: "0",
  },
  {
    id: "event",
    name: "Events",
    icon: "event",
    iconDefault: eventsImg,
    iconActive: eventsActiveImg,
    badge: "0",
  },
  {
    id: "home",
    name: "Home",
    icon: "home",
    iconDefault: homeImg,
    iconActive: homeActiveImg,
    badge: "0",
  },
  {
    id: "vip",
    name: "Rewards",
    icon: "reward",
    iconDefault: rewardsImg,
    iconActive: rewardsActiveImg,
  },
  {
    id: "profile",
    name: "Account",
    icon: "profile",
    iconDefault: accountImg,
    iconActive: accountActiveImg,
    badge: "0",
  },
];

export const BottomNavigation: React.FC<BottomNavigationProps> = memo(
  ({
    className = "",
    activeItem = "home",
    onItemClick,
  }: BottomNavigationProps) => {
    const t = useT();
    const [vipBadgeCount] = useGlobalValue("vipBadgeCount");

    // 划入动画状态
    const [visible, setVisible] = useState(false);
    const hasAnimated = useRef(false);

    // 首次加载后延迟触发划入动画
    useEffect(() => {
      if (hasAnimated.current) return;
      const timer = setTimeout(() => {
        setVisible(true);
        hasAnimated.current = true;
      }, 150);
      return () => clearTimeout(timer);
    }, []);

    // 异步获取 VIP Badge 数据
    useEffect(() => {
      const fetchVipBadgeCount = async () => {
        try {
          const response = await getVipList();
          if (response?.data?.data.canClaimCount !== undefined) {
            setGlobalValue("vipBadgeCount", response.data.data.canClaimCount);
          }
        } catch (error) {
          console.error("Failed to fetch VIP badge count:", error);
        }
      };

      const timer = setTimeout(() => {
        fetchVipBadgeCount();
      }, 500);

      return () => clearTimeout(timer);
    }, []);

    const handleItemClick = useCallback(
      (item: NavigationItem) => {
        item.onClick?.();
        onItemClick?.(item.id);
      },
      [onItemClick]
    );

    const baseItems = defaultItems;

    const navigationItems = baseItems.map(item => {
      let translatedName = item.name;
      switch (item.id) {
        case "home":
          translatedName = t("navigation.home");
          break;
        case "event":
          translatedName = t("navigation.events");
          break;
        case "agent-center":
          translatedName = t("agent.invite");
          break;
        case "vip":
          translatedName = t("vip.rewards");
          break;
        case "profile":
          translatedName = t("navigation.account");
          break;
      }

      if (item.id === "vip") {
        return {
          ...item,
          name: translatedName,
          badge: vipBadgeCount > 0 ? String(vipBadgeCount) : undefined,
        };
      }
      return {
        ...item,
        name: translatedName,
        badge: undefined,
      };
    });

    const navClass = [
      "bottom-navigation",
      visible ? "bottom-navigation--visible" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <nav className={navClass}>
        <div className="bottom-navigation__container">
          {navigationItems.map(item => {
            const isActive = activeItem === item.id;
            const defaultSrc = item.iconDefault as string;
            const activeSrc = item.iconActive as string;
            const src = isActive ? activeSrc : defaultSrc;

            return (
              <button
                key={item.id}
                className={[
                  "bottom-navigation__item",
                  isActive ? "active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleItemClick(item)}
                aria-label={item.name}
                aria-current={isActive ? "page" : undefined}
              >
                <div className="bottom-navigation__icon">
                  <img
                    className="bottom-navigation__icon-img"
                    src={src}
                    alt=""
                  />
                </div>
                <span className="bottom-navigation__label">{item.name}</span>
                {item.badge && item.badge !== "0" && (
                  <span className="bottom-navigation__badge">{item.badge}</span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    );
  }
);

BottomNavigation.displayName = "BottomNavigation";

export default BottomNavigation;
